import { useCallback, useEffect, useRef, useState } from "react";
import analytics from "../../Services/analyticsService";
import MascotAvatar from "./MascotAvatar";
import useMyMascot from "./useMyMascot";
import "./mascot.css";

const REACTIONS = [
  { mood: "celebrate", line: "Hi!" },
  { mood: "squish", squash: true },
  { mood: "dizzy", line: "Whoa…" },
  { mood: "whee", line: "Wheee!" },
  { mood: "happy", line: "Where is everyone?" },
  { mood: "thinking", line: "Ready when they are." },
  { mood: "happy" },
];
const REACT_MS = 1300;
const IDLE_MS = 20000;
const WANDER_MS = 4200;

/**
 * The player's mascot delivering the lobby status in a speech bubble. Tap it
 * for a small reaction; leave the lobby idle and it hops onto the bubble,
 * strolls to the end, spins and comes back.
 */
export default function LobbyMascot({ message, className = "" }) {
  const mascot = useMyMascot();
  const [reaction, setReaction] = useState(null);
  const [taps, setTaps] = useState(0);
  const [wander, setWander] = useState(null);
  const last = useRef(-1);
  const reactTimer = useRef(null);
  const idleTimer = useRef(null);
  const wanderTimer = useRef(null);
  const rowRef = useRef(null);
  const bubbleRef = useRef(null);
  const bodyRef = useRef(null);

  const armIdle = useCallback(() => {
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      const row = rowRef.current;
      const bubble = bubbleRef.current;
      const body = bodyRef.current;
      if (!row || !bubble || !body || document.hidden) return armIdle();
      const bodyW = body.offsetWidth;
      setWander({
        "--hx": `${bubble.offsetLeft}px`,
        "--hy": `${-bubble.offsetHeight}px`,
        "--wx": `${row.offsetWidth - bodyW}px`,
      });
      wanderTimer.current = setTimeout(() => {
        setWander(null);
        armIdle();
      }, WANDER_MS);
    }, IDLE_MS);
  }, []);

  useEffect(() => {
    armIdle();
    return () => {
      clearTimeout(idleTimer.current);
      clearTimeout(reactTimer.current);
      clearTimeout(wanderTimer.current);
    };
  }, [armIdle]);

  const react = () => {
    analytics.track("mascot_interacted", { surface: "lobby", action: "tap" });
    clearTimeout(wanderTimer.current);
    setWander(null);
    let i = Math.floor(Math.random() * REACTIONS.length);
    if (i === last.current) i = (i + 1) % REACTIONS.length;
    last.current = i;
    setReaction(REACTIONS[i]);
    setTaps((n) => n + 1);
    clearTimeout(reactTimer.current);
    reactTimer.current = setTimeout(() => setReaction(null), REACT_MS);
    armIdle();
  };

  const mood = reaction?.mood || (wander ? "whee" : "calm");

  return (
    <div ref={rowRef} className={`relative flex items-end gap-2.5 ${className}`}>
      <button
        type="button"
        onClick={react}
        aria-label="Your Namey. Tap to play"
        className="relative z-10 flex-shrink-0 select-none"
        style={{ WebkitTapHighlightColor: "transparent" }}
      >
        <div ref={bodyRef} className={wander ? "nm-wander" : ""} style={wander || undefined}>
          <div key={taps} className={taps ? "nm-react" : ""}>
            <div
              style={{
                transform: reaction?.squash ? "scale(1.15, 0.8)" : "none",
                transformOrigin: "50% 100%",
                transition: "transform 0.15s ease-out",
              }}
            >
              <MascotAvatar mascot={mascot} mood={mood} size={mascot.base === "pencil" ? 28 : 42} shadow={false} />
            </div>
          </div>
        </div>
      </button>
      <div
        ref={bubbleRef}
        className="relative mb-1 flex-1 min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
        aria-live="polite"
      >
        <span
          aria-hidden="true"
          className="absolute -left-[6px] bottom-3 h-2.5 w-2.5 rotate-45 border-b border-l border-gray-200 bg-gray-50"
        />
        {reaction?.line ? (
          <span key={taps} className="nm-pop inline-block font-semibold text-gray-700">
            {reaction.line}
          </span>
        ) : (
          message
        )}
      </div>
    </div>
  );
}
